<template>
  <div class="maintenance-statistics-page">
    <el-tabs v-model="activeTab" class="statistics-tabs" @tab-click="handleTabChange">
      <el-tab-pane label="年度统计" name="year">
        <el-card shadow="never" class="filter-card">
          <el-form :inline="true" size="small" :model="annualQuery" class="statistics-query">
            <el-form-item label="统计年度">
              <el-date-picker
                v-model="annualQuery.year"
                type="year"
                value-format="yyyy"
                placeholder="选择年度"
                style="width: 140px"
              />
            </el-form-item>
            <el-form-item label="设备名称">
              <el-input v-model="annualQuery.deviceName" clearable placeholder="请输入设备名称" style="width: 180px" />
            </el-form-item>
            <el-form-item label="维保类型">
              <el-select v-model="annualQuery.maintenanceType" clearable placeholder="全部" style="width: 130px">
                <el-option label="日常维保" value="routine" />
                <el-option label="定期维保" value="periodic" />
                <el-option label="季节性维保" value="seasonal" />
              </el-select>
            </el-form-item>
            <el-form-item label="风险类型">
              <el-select v-model="annualQuery.riskType" clearable placeholder="全部" style="width: 140px">
                <el-option label="逾期" value="overdue" />
                <el-option label="临期" value="dueSoon" />
                <el-option label="延期申请中" value="delayAudit" />
                <el-option label="完成待审核" value="completeAudit" />
                <el-option label="超期完成" value="overdueCompleted" />
              </el-select>
            </el-form-item>
            <el-form-item class="query-actions">
              <el-button type="primary" :loading="loading" @click="loadAnnualStatistics">查询</el-button>
              <el-button @click="resetAnnualQuery">重置</el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <el-row :gutter="12" class="metric-row">
          <el-col v-for="item in annualMetrics" :key="item.key" :span="3">
            <div class="metric-card" :class="item.type">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="12" class="content-row">
          <el-col :span="16">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-header">
                <span>年度月度趋势</span>
              </div>
              <div ref="annualTrendChart" class="trend-chart" />
            </el-card>
          </el-col>
          <el-col :span="8">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-header">
                <span>年度风险统计</span>
              </div>
              <div class="risk-grid">
                <div class="risk-item danger">
                  <span>年度逾期</span>
                  <strong>{{ annualRiskValue('yearOverdue') }}</strong>
                </div>
                <div class="risk-item warning">
                  <span>7天临期</span>
                  <strong>{{ annualRiskValue('dueSoon') }}</strong>
                </div>
                <div class="risk-item warning">
                  <span>延期申请中</span>
                  <strong>{{ annualRiskValue('delayAuditing') }}</strong>
                </div>
                <div class="risk-item primary">
                  <span>完成待审核</span>
                  <strong>{{ annualRiskValue('completeAuditing') }}</strong>
                </div>
                <div class="risk-item info">
                  <span>超期完成</span>
                  <strong>{{ annualRiskValue('overdueCompleted') }}</strong>
                </div>
                <div class="risk-item primary">
                  <span>审核通过数</span>
                  <strong>{{ value(annualStats.summary.yearApproved) }}</strong>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-header">
            <span>年度计划明细</span>
            <el-radio-group v-model="annualDetailTab" size="small">
              <el-radio-button label="current">全部</el-radio-button>
              <el-radio-button label="overdue">逾期</el-radio-button>
              <el-radio-button label="delayAudit">延期申请中</el-radio-button>
              <el-radio-button label="completeAudit">完成待审核</el-radio-button>
            </el-radio-group>
          </div>
          <el-table :data="annualDetailRows" border stripe v-loading="loading" style="width: 100%">
            <el-table-column prop="planNo" label="计划编号" min-width="150" show-overflow-tooltip />
            <el-table-column prop="deviceName" label="设备名称" min-width="150" show-overflow-tooltip />
            <el-table-column label="维保类型" width="110">
              <template slot-scope="{ row }">{{ maintenanceTypeText(row.maintenanceType) }}</template>
            </el-table-column>
            <el-table-column label="计划日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.planDate) }}</template>
            </el-table-column>
            <el-table-column label="截止日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.deadline) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="executorName" label="执行人" width="100" show-overflow-tooltip />
            <el-table-column label="开始时间" width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.startTime) }}</template>
            </el-table-column>
            <el-table-column label="完成时间" width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.endTime) }}</template>
            </el-table-column>
            <el-table-column label="风险" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="riskTagType(row)" size="small">{{ riskText(row) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="月度统计" name="month">
        <el-card shadow="never" class="filter-card">
          <el-form :inline="true" size="small" :model="monthlyQuery" class="statistics-query">
            <el-form-item label="统计月份">
              <el-date-picker
                v-model="monthlyQuery.month"
                type="month"
                value-format="yyyy-MM"
                placeholder="选择月份"
                style="width: 150px"
              />
            </el-form-item>
            <el-form-item label="设备名称">
              <el-input v-model="monthlyQuery.deviceName" clearable placeholder="请输入设备名称" style="width: 180px" />
            </el-form-item>
            <el-form-item label="维保类型">
              <el-select v-model="monthlyQuery.maintenanceType" clearable placeholder="全部" style="width: 130px">
                <el-option label="日常维保" value="routine" />
                <el-option label="定期维保" value="periodic" />
                <el-option label="季节性维保" value="seasonal" />
              </el-select>
            </el-form-item>
            <el-form-item label="风险类型">
              <el-select v-model="monthlyQuery.riskType" clearable placeholder="全部" style="width: 140px">
                <el-option label="逾期" value="overdue" />
                <el-option label="临期" value="dueSoon" />
                <el-option label="延期申请中" value="delayAudit" />
                <el-option label="完成待审核" value="completeAudit" />
                <el-option label="超期完成" value="overdueCompleted" />
              </el-select>
            </el-form-item>
            <el-form-item class="query-actions">
              <el-button type="primary" :loading="loading" @click="loadMonthlyStatistics">查询</el-button>
              <el-button @click="resetMonthlyQuery">重置</el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <el-row :gutter="12" class="metric-row">
          <el-col v-for="item in monthlyMetrics" :key="item.key" :span="3">
            <div class="metric-card" :class="item.type">
              <div class="metric-label">{{ item.label }}</div>
              <div class="metric-value">{{ item.value }}</div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="12" class="content-row">
          <el-col :span="8">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-header">
                <span>月度风险统计</span>
              </div>
              <div class="risk-grid monthly-risk-grid">
                <div class="risk-item danger">
                  <span>本月逾期</span>
                  <strong>{{ monthlyRiskValue('monthOverdue') }}</strong>
                </div>
                <div class="risk-item warning">
                  <span>7天临期</span>
                  <strong>{{ monthlyRiskValue('dueSoon') }}</strong>
                </div>
                <div class="risk-item warning">
                  <span>延期申请中</span>
                  <strong>{{ monthlyRiskValue('delayAuditing') }}</strong>
                </div>
                <div class="risk-item primary">
                  <span>完成待审核</span>
                  <strong>{{ monthlyRiskValue('completeAuditing') }}</strong>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="16">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-header">
                <span>月度状态汇总</span>
              </div>
              <el-table :data="monthlySummaryRows" border stripe v-loading="loading" style="width: 100%">
                <el-table-column prop="name" label="状态" min-width="120" />
                <el-table-column prop="count" label="数量" width="120" align="center" />
                <el-table-column prop="rate" label="占比" width="120" align="center" />
              </el-table>
            </el-card>
          </el-col>
        </el-row>

        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-header">
            <span>月度维保执行跟踪</span>
            <el-radio-group v-model="monthlyDetailTab" size="small">
              <el-radio-button label="current">全部</el-radio-button>
              <el-radio-button label="overdue">逾期</el-radio-button>
              <el-radio-button label="delayAudit">延期申请中</el-radio-button>
              <el-radio-button label="completeAudit">完成待审核</el-radio-button>
            </el-radio-group>
          </div>
          <el-table :data="monthlyDetailRows" border stripe v-loading="loading" style="width: 100%">
            <el-table-column prop="planNo" label="计划编号" min-width="150" show-overflow-tooltip />
            <el-table-column prop="deviceName" label="设备名称" min-width="150" show-overflow-tooltip />
            <el-table-column label="维保类型" width="110">
              <template slot-scope="{ row }">{{ maintenanceTypeText(row.maintenanceType) }}</template>
            </el-table-column>
            <el-table-column label="计划日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.planDate) }}</template>
            </el-table-column>
            <el-table-column label="截止日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.deadline) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="executorName" label="执行人" width="100" show-overflow-tooltip />
            <el-table-column label="开始时间" width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.startTime) }}</template>
            </el-table-column>
            <el-table-column label="完成时间" width="160">
              <template slot-scope="{ row }">{{ formatDateTime(row.endTime) }}</template>
            </el-table-column>
            <el-table-column label="风险" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="riskTagType(row)" size="small">{{ riskText(row) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-header">
            <span>下月维保计划</span>
          </div>
          <el-table :data="monthlyStats.nextMonthPlans" border stripe v-loading="loading" style="width: 100%">
            <el-table-column prop="planNo" label="计划编号" min-width="150" show-overflow-tooltip />
            <el-table-column prop="deviceName" label="设备名称" min-width="160" show-overflow-tooltip />
            <el-table-column prop="content" label="维保内容" min-width="220" show-overflow-tooltip />
            <el-table-column label="维保类型" width="110">
              <template slot-scope="{ row }">{{ maintenanceTypeText(row.maintenanceType) }}</template>
            </el-table-column>
            <el-table-column label="计划日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.planDate) }}</template>
            </el-table-column>
            <el-table-column label="截止日期" width="110">
              <template slot-scope="{ row }">{{ formatDate(row.deadline) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110" align="center">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDispatchStatistics } from './api'

function formatYmd(date) {
  const year = date.getFullYear()
  const month = (`0${date.getMonth() + 1}`).slice(-2)
  const day = (`0${date.getDate()}`).slice(-2)
  return `${year}-${month}-${day}`
}

function currentMonthValue() {
  const now = new Date()
  return `${now.getFullYear()}-${(`0${now.getMonth() + 1}`).slice(-2)}`
}

function monthRange(monthValue) {
  const value = monthValue || currentMonthValue()
  const parts = value.split('-')
  const year = Number(parts[0])
  const month = Number(parts[1])
  return {
    year: String(year),
    startDate: formatYmd(new Date(year, month - 1, 1)),
    endDate: formatYmd(new Date(year, month, 0)),
  }
}

function yearRange(yearValue) {
  const year = yearValue || String(new Date().getFullYear())
  return {
    year,
    startDate: `${year}-01-01`,
    endDate: `${year}-12-31`,
  }
}

function emptyStatistics() {
  return {
    summary: {},
    riskSummary: {},
    yearTrend: [],
    currentMonth: {},
    currentMonthPlans: [],
    nextMonthPlans: [],
    overduePlans: [],
    delayAuditPlans: [],
    completeAuditPlans: [],
  }
}

export default {
  name: 'MaintenanceStatistics',
  data() {
    const now = new Date()
    return {
      activeTab: 'year',
      loading: false,
      annualQuery: {
        year: String(now.getFullYear()),
        deviceName: '',
        maintenanceType: '',
        riskType: '',
      },
      monthlyQuery: {
        month: currentMonthValue(),
        deviceName: '',
        maintenanceType: '',
        riskType: '',
      },
      annualDetailTab: 'current',
      monthlyDetailTab: 'current',
      annualStats: emptyStatistics(),
      monthlyStats: emptyStatistics(),
      annualLoaded: false,
      monthlyLoaded: false,
      annualChart: null,
    }
  },
  computed: {
    annualMetrics() {
      const summary = this.annualStats.summary || {}
      const risk = this.annualStats.riskSummary || {}
      return [
        { key: 'yearTotal', label: '年度计划总数', value: this.value(summary.yearTotal), type: 'primary' },
        { key: 'yearCompletionRate', label: '年度完成率', value: this.value(summary.yearCompletionRate), type: 'success' },
        { key: 'yearApproved', label: '审核通过数', value: this.value(summary.yearApproved), type: '' },
        { key: 'yearOverdue', label: '年度逾期数', value: this.value(summary.yearOverdue), type: 'danger' },
        { key: 'delayAuditing', label: '延期申请中', value: this.value(risk.delayAuditing), type: 'warning' },
        { key: 'completeAuditing', label: '完成待审核', value: this.value(risk.completeAuditing), type: 'warning' },
        { key: 'overdueCompleted', label: '超期完成', value: this.value(risk.overdueCompleted), type: '' },
        { key: 'dueSoon', label: '7天临期', value: this.value(risk.dueSoon), type: 'warning' },
      ]
    },
    monthlyMetrics() {
      const summary = this.monthlyStats.summary || {}
      const currentMonth = this.monthlyStats.currentMonth || {}
      return [
        { key: 'currentMonthTotal', label: '本月计划数', value: this.value(summary.currentMonthTotal), type: 'primary' },
        { key: 'currentMonthCompletionRate', label: '本月完成率', value: this.value(summary.currentMonthCompletionRate), type: 'success' },
        { key: 'yearApproved', label: '审核通过数', value: this.value(currentMonth.completed), type: '' },
        { key: 'monthOverdue', label: '本月逾期数', value: this.monthlyRiskValue('monthOverdue'), type: 'danger' },
        { key: 'pending', label: '待执行', value: this.value(currentMonth.pending), type: 'warning' },
        { key: 'inProgress', label: '执行中', value: this.value(currentMonth.inProgress), type: 'primary' },
        { key: 'delayAuditing', label: '延期申请中', value: this.value(currentMonth.delayAuditing), type: 'warning' },
        { key: 'completeAuditing', label: '完成待审核', value: this.value(currentMonth.completeAuditing), type: 'warning' },
      ]
    },
    annualDetailRows() {
      if (this.annualDetailTab === 'overdue') return this.annualStats.overduePlans || []
      if (this.annualDetailTab === 'delayAudit') return this.annualStats.delayAuditPlans || []
      if (this.annualDetailTab === 'completeAudit') return this.annualStats.completeAuditPlans || []
      return this.annualStats.currentMonthPlans || []
    },
    monthlyDetailRows() {
      if (this.monthlyDetailTab === 'overdue') return this.monthlyStats.overduePlans || []
      if (this.monthlyDetailTab === 'delayAudit') return this.monthlyStats.delayAuditPlans || []
      if (this.monthlyDetailTab === 'completeAudit') return this.monthlyStats.completeAuditPlans || []
      return this.monthlyStats.currentMonthPlans || []
    },
    monthlySummaryRows() {
      const currentMonth = this.monthlyStats.currentMonth || {}
      const total = this.numberValue(currentMonth.total)
      return [
        this.buildSummaryRow('待执行', currentMonth.pending, total),
        this.buildSummaryRow('执行中', currentMonth.inProgress, total),
        this.buildSummaryRow('已完成', currentMonth.completed, total),
        this.buildSummaryRow('延期申请中', currentMonth.delayAuditing, total),
        this.buildSummaryRow('完成待审核', currentMonth.completeAuditing, total),
      ]
    },
  },
  mounted() {
    this.loadAnnualStatistics()
    window.addEventListener('resize', this.resizeChart)
  },
  activated() {
    if (this.activeTab === 'year') {
      this.loadAnnualStatistics()
    } else {
      this.loadMonthlyStatistics()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.annualChart) {
      this.annualChart.dispose()
      this.annualChart = null
    }
  },
  methods: {
    handleTabChange() {
      if (this.activeTab === 'year') {
        if (!this.annualLoaded) {
          this.loadAnnualStatistics()
        } else {
          this.$nextTick(this.renderAnnualTrendChart)
        }
        return
      }
      if (!this.monthlyLoaded) {
        this.loadMonthlyStatistics()
      }
    },
    loadAnnualStatistics() {
      this.loading = true
      getDispatchStatistics(this.buildAnnualQuery())
        .then((res) => {
          this.annualStats = this.parseStatisticsResponse(res)
          this.annualLoaded = true
          this.$nextTick(this.renderAnnualTrendChart)
        })
        .catch(() => {
          this.$message.error('获取年度统计失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadMonthlyStatistics() {
      this.loading = true
      getDispatchStatistics(this.buildMonthlyQuery())
        .then((res) => {
          this.monthlyStats = this.parseStatisticsResponse(res)
          this.monthlyLoaded = true
        })
        .catch(() => {
          this.$message.error('获取月度统计失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    parseStatisticsResponse(res) {
      const body = res && res.data
      const data = (body && body.data) || {}
      return {
        summary: data.summary || {},
        riskSummary: data.riskSummary || {},
        yearTrend: data.yearTrend || [],
        currentMonth: data.currentMonth || {},
        currentMonthPlans: data.currentMonthPlans || [],
        nextMonthPlans: data.nextMonthPlans || [],
        overduePlans: data.overduePlans || [],
        delayAuditPlans: data.delayAuditPlans || [],
        completeAuditPlans: data.completeAuditPlans || [],
      }
    },
    buildAnnualQuery() {
      const range = yearRange(this.annualQuery.year)
      return {
        year: range.year,
        month: `${range.year}-01`,
        startDate: range.startDate,
        endDate: range.endDate,
        deviceName: this.annualQuery.deviceName,
        maintenanceType: this.annualQuery.maintenanceType,
        riskType: this.annualQuery.riskType,
      }
    },
    buildMonthlyQuery() {
      const range = monthRange(this.monthlyQuery.month)
      return {
        year: range.year,
        month: this.monthlyQuery.month,
        startDate: range.startDate,
        endDate: range.endDate,
        deviceName: this.monthlyQuery.deviceName,
        maintenanceType: this.monthlyQuery.maintenanceType,
        riskType: this.monthlyQuery.riskType,
      }
    },
    resetAnnualQuery() {
      this.annualQuery = {
        year: String(new Date().getFullYear()),
        deviceName: '',
        maintenanceType: '',
        riskType: '',
      }
      this.annualDetailTab = 'current'
      this.loadAnnualStatistics()
    },
    resetMonthlyQuery() {
      this.monthlyQuery = {
        month: currentMonthValue(),
        deviceName: '',
        maintenanceType: '',
        riskType: '',
      }
      this.monthlyDetailTab = 'current'
      this.loadMonthlyStatistics()
    },
    renderAnnualTrendChart() {
      if (!this.$refs.annualTrendChart) return
      if (!this.annualChart) {
        this.annualChart = echarts.init(this.$refs.annualTrendChart)
      }
      const trend = this.annualStats.yearTrend || []
      const months = trend.map((item) => item.monthName)
      const option = {
        color: ['#909399', '#409eff', '#67c23a', '#e6a23c', '#7c3aed', '#f56c6c'],
        tooltip: { trigger: 'axis' },
        legend: {
          top: 0,
          data: ['待执行', '执行中', '已完成', '延期申请中', '完成待审核', '逾期'],
        },
        grid: { left: 40, right: 20, top: 42, bottom: 34 },
        xAxis: { type: 'category', data: months },
        yAxis: { type: 'value', minInterval: 1 },
        series: [
          { name: '待执行', type: 'bar', stack: 'total', data: trend.map((item) => this.numberValue(item.pending)) },
          { name: '执行中', type: 'bar', stack: 'total', data: trend.map((item) => this.numberValue(item.inProgress)) },
          { name: '已完成', type: 'bar', stack: 'total', data: trend.map((item) => this.numberValue(item.completed)) },
          { name: '延期申请中', type: 'bar', stack: 'total', data: trend.map((item) => this.numberValue(item.delayAuditing)) },
          { name: '完成待审核', type: 'bar', stack: 'total', data: trend.map((item) => this.numberValue(item.completeAuditing)) },
          { name: '逾期', type: 'line', data: trend.map((item) => this.numberValue(item.overdue)) },
        ],
      }
      this.annualChart.setOption(option, true)
      this.resizeChart()
    },
    resizeChart() {
      if (this.annualChart) this.annualChart.resize()
    },
    annualRiskValue(key) {
      return this.value((this.annualStats.riskSummary || {})[key])
    },
    monthlyRiskValue(key) {
      return this.value((this.monthlyStats.riskSummary || {})[key])
    },
    buildSummaryRow(name, count, total) {
      const value = this.numberValue(count)
      const rate = total > 0 ? `${((value * 100) / total).toFixed(1)}%` : '0.0%'
      return { name, count: value, rate }
    },
    value(value) {
      return value == null || value === '' ? 0 : value
    },
    numberValue(value) {
      const num = Number(value)
      return Number.isNaN(num) ? 0 : num
    },
    maintenanceTypeText(type) {
      if (type === 'routine') return '日常维保'
      if (type === 'periodic') return '定期维保'
      if (type === 'seasonal') return '季节性维保'
      return type || '-'
    },
    statusText(status) {
      if (status === 0) return '待执行'
      if (status === 1) return '执行中'
      if (status === 2) return '已完成'
      if (status === 3) return '延期申请中'
      if (status === 4) return '完成待审核'
      return '-'
    },
    statusTagType(status) {
      if (status === 0) return 'info'
      if (status === 1) return 'primary'
      if (status === 2) return 'success'
      if (status === 3) return 'warning'
      if (status === 4) return 'warning'
      return 'info'
    },
    riskText(row) {
      if (this.isOpenOverdue(row)) return '逾期'
      if (this.isDueSoon(row)) return '临期'
      if (this.isOverdueCompleted(row)) return '超期'
      return '正常'
    },
    riskTagType(row) {
      if (this.isOpenOverdue(row)) return 'danger'
      if (this.isDueSoon(row)) return 'warning'
      if (this.isOverdueCompleted(row)) return 'warning'
      return 'success'
    },
    isOpenOverdue(row) {
      const deadline = this.parseDateValue(row && row.deadline)
      return deadline && deadline.getTime() < this.todayTime() && [0, 1, 3, 4].indexOf(row.status) > -1
    },
    isDueSoon(row) {
      const deadline = this.parseDateValue(row && row.deadline)
      const today = this.todayTime()
      const dueEnd = today + 8 * 24 * 60 * 60 * 1000
      return deadline && deadline.getTime() >= today && deadline.getTime() < dueEnd && [0, 1, 3, 4].indexOf(row.status) > -1
    },
    isOverdueCompleted(row) {
      const deadline = this.parseDateValue(row && row.deadline)
      const endTime = this.parseDateValue(row && row.endTime)
      return row && row.status === 2 && deadline && endTime && endTime.getTime() > deadline.getTime()
    },
    todayTime() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    },
    parseDateValue(value) {
      if (!value) return null
      if (typeof value === 'number') return new Date(value)
      if (typeof value === 'string') return new Date(value.replace(/-/g, '/'))
      return new Date(value)
    },
    formatDate(value) {
      const date = this.parseDateValue(value)
      if (!date || Number.isNaN(date.getTime())) return '-'
      return formatYmd(date)
    },
    formatDateTime(value) {
      const date = this.parseDateValue(value)
      if (!date || Number.isNaN(date.getTime())) return '-'
      const hour = (`0${date.getHours()}`).slice(-2)
      const minute = (`0${date.getMinutes()}`).slice(-2)
      const second = (`0${date.getSeconds()}`).slice(-2)
      return `${formatYmd(date)} ${hour}:${minute}:${second}`
    },
  },
}
</script>

<style scoped>
.maintenance-statistics-page {
  padding: 20px;
  background: #f5f7fa;
}
.statistics-tabs /deep/ .el-tabs__header {
  margin-bottom: 12px;
  padding: 0 16px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fff;
}
.filter-card,
.panel-card {
  margin-bottom: 12px;
  border-radius: 6px;
}
.statistics-query {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}
.statistics-query /deep/ .el-form-item {
  margin-right: 14px;
  margin-bottom: 10px;
}
.query-actions {
  margin-right: 0;
}
.metric-row {
  margin-bottom: 12px;
}
.metric-card {
  min-height: 92px;
  padding: 13px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fff;
}
.metric-card.primary {
  border-color: #d9ecff;
  background: #f5faff;
}
.metric-card.success {
  border-color: #e1f3d8;
  background: #f5fbf1;
}
.metric-card.warning {
  border-color: #faecd8;
  background: #fffaf2;
}
.metric-card.danger {
  border-color: #fde2e2;
  background: #fff7f7;
}
.metric-label {
  color: #606266;
  font-size: 13px;
}
.metric-value {
  margin-top: 12px;
  color: #303133;
  font-size: 25px;
  font-weight: 600;
  line-height: 1;
}
.content-row {
  margin-bottom: 12px;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  font-weight: 600;
}
.trend-chart {
  width: 100%;
  height: 320px;
}
.risk-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.monthly-risk-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.risk-item {
  min-height: 86px;
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fff;
}
.risk-item span {
  color: #606266;
  font-size: 13px;
}
.risk-item strong {
  display: block;
  margin-top: 10px;
  color: #303133;
  font-size: 24px;
}
.risk-item.danger {
  border-color: #fde2e2;
  background: #fff7f7;
}
.risk-item.warning {
  border-color: #faecd8;
  background: #fffaf2;
}
.risk-item.primary {
  border-color: #d9ecff;
  background: #f5faff;
}
.risk-item.info {
  border-color: #e9e9eb;
  background: #fafafa;
}
@media (max-width: 1200px) {
  .metric-row .el-col,
  .content-row .el-col {
    width: 100%;
    margin-bottom: 12px;
  }
}
</style>
